import React, { useState, useEffect } from 'react';

// Fake data that cycles to look live
const EMOTIONS = ['Concentrating', 'Calm', 'Focused', 'Neutral'];
const EMOTION_COLORS = { Concentrating: '#0a9688', Calm: '#3b82f6', Focused: '#0cb8a0', Neutral: '#9ca3af' };
const EMOTION_EMOJIS = { Concentrating: '🧐', Calm: '😌', Focused: '🎯', Neutral: '😐' };

const COACH_TIPS = [
  { icon: '😮‍💨', color: '#0a9688', headline: 'Take a short breathing break', detail: 'Your attention has been strong for 47 minutes. A 2-minute reset will help you sustain focus through the afternoon.' },
  { icon: '💧', color: '#3b82f6', headline: 'Time to hydrate', detail: 'Your blink rate is slightly low at 12/min. Drinking water and looking away from the screen for 20 seconds can help.' },
  { icon: '✨', color: '#0cb8a0', headline: 'You are above your usual level', detail: 'Your attention score of 87 is 8 points above your daily average. This is a great time for deep work.' },
  { icon: '🧘', color: '#7c3aed', headline: 'Check your posture', detail: 'You have been leaning forward for a while. Roll your shoulders back and sit upright to reduce fatigue.' },
];

const FAKE_CHART = [
  { t: '9:00', a: 72 }, { t: '9:15', a: 78 }, { t: '9:30', a: 85 }, { t: '9:45', a: 81 },
  { t: '10:00', a: 88 }, { t: '10:15', a: 76 }, { t: '10:30', a: 82 }, { t: '10:45', a: 90 },
  { t: '11:00', a: 84 }, { t: '11:15', a: 79 }, { t: '11:30', a: 86 }, { t: '11:45', a: 91 },
];

function MiniChart({ data }) {
  const W = 380, H = 80, pad = 8;
  const maxA = 100, minA = 60;
  const pts = data.map((d, i) => {
    const x = pad + (i / (data.length - 1)) * (W - pad * 2);
    const y = H - pad - ((d.a - minA) / (maxA - minA)) * (H - pad * 2);
    return `${x},${y}`;
  });
  const area = `M${pts.join('L')}L${W - pad},${H}L${pad},${H}Z`;
  const line = `M${pts.join('L')}`;
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ display:'block' }}>
      <defs>
        <linearGradient id="demoGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#0cb8a0" stopOpacity="0.3"/>
          <stop offset="100%" stopColor="#0cb8a0" stopOpacity="0"/>
        </linearGradient>
      </defs>
      <path d={area} fill="url(#demoGrad)"/>
      <path d={line} fill="none" stroke="#0cb8a0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      {data.map((d, i) => {
        const x = pad + (i / (data.length - 1)) * (W - pad * 2);
        const y = H - pad - ((d.a - minA) / (maxA - minA)) * (H - pad * 2);
        return i % 3 === 0 ? (
          <text key={i} x={x} y={H - 2} textAnchor="middle" fontSize="8" fill="#9ca3af">{d.t}</text>
        ) : null;
      })}
    </svg>
  );
}

function AttentionArc({ score }) {
  const r = 32, cx = 40, cy = 40, sw = 5;
  const circ = 2 * Math.PI * r;
  const fill = circ * (score / 100);
  const col = score >= 70 ? '#0cb8a0' : '#f59e0b';
  return (
    <svg width={80} height={80}>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e5e7eb" strokeWidth={sw}/>
      <circle cx={cx} cy={cy} r={r} fill="none" stroke={col} strokeWidth={sw}
        strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dasharray 1s ease' }}/>
      <text x={cx} y={cx + 5} textAnchor="middle" fontSize="14" fill={col} fontWeight="bold">{score}</text>
      <text x={cx} y={cx + 16} textAnchor="middle" fontSize="7" fill="#9ca3af">/100</text>
    </svg>
  );
}

// ── Live view mockup ────────────────────────────────────────────────────────
function LiveMockup({ emotion, attention, tick }) {
  const ec = EMOTION_COLORS[emotion] || '#9ca3af';
  const blinkRate = 14 + (tick % 5);
  const perclos = 2.1 + (tick % 3) * 0.4;

  return (
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:0, borderRadius:12, overflow:'hidden', border:'1px solid #e5e7eb', background:'#f9fafb', fontSize:'0.7rem', fontFamily:"'Nunito',sans-serif" }}>
      {/* Left — camera + emotion */}
      <div style={{ borderRight:'1px solid #e5e7eb', display:'flex', flexDirection:'column', gap:0 }}>
        {/* Fake camera */}
        <div style={{ background:'#1a2332', position:'relative', height:150, flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
          {/* Silhouette */}
          <svg width="80" height="110" viewBox="0 0 80 110" style={{ opacity:0.25 }}>
            <circle cx="40" cy="30" r="22" fill="#ffffff"/>
            <path d="M0 110 Q0 70 40 70 Q80 70 80 110Z" fill="#ffffff"/>
          </svg>
          {/* Mesh dots overlay */}
          {[...Array(18)].map((_, i) => (
            <div key={i} style={{
              position:'absolute',
              left: `${28 + (i % 6) * 8}%`,
              top: `${20 + Math.floor(i / 6) * 20}%`,
              width: 3, height: 3, borderRadius:'50%',
              background: '#0cb8a0', opacity: 0.6,
            }}/>
          ))}
          {/* Face detected badge */}
          <div style={{ position:'absolute', top:8, right:8, background:'rgba(255,255,255,0.12)', borderRadius:20, padding:'3px 8px', color:'#0cb8a0', fontSize:'0.58rem', display:'flex', alignItems:'center', gap:4 }}>
            <div style={{ width:5, height:5, borderRadius:'50%', background:'#0cb8a0', boxShadow:'0 0 6px #0cb8a0' }}/>
            Face detected
          </div>
          {/* Stats bar */}
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'rgba(0,0,0,0.5)', padding:'4px 8px', display:'flex', gap:10 }}>
            {[`👁 EAR: 0.31`, `⚡ ${blinkRate}/min`, `😴 ${perclos.toFixed(1)}%`].map(s => (
              <span key={s} style={{ color:'rgba(255,255,255,0.7)', fontSize:'0.55rem' }}>{s}</span>
            ))}
          </div>
        </div>

        {/* Emotion card */}
        <div style={{ padding:'10px 12px', background:'#ffffff', flex:1 }}>
          <div style={{ fontSize:'0.52rem', letterSpacing:'0.1em', textTransform:'uppercase', color:'#9ca3af', marginBottom:6 }}>Detected Emotion</div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:8 }}>
            <span style={{ fontSize:'1.3rem' }}>{EMOTION_EMOJIS[emotion]}</span>
            <span style={{ fontFamily:"'Fraunces',serif", fontSize:'1.1rem', color: ec }}>{emotion}</span>
          </div>
          {[['😐 Neutral','8%'],['😌 Calm','5%'],['😊 Happy','3%'],['😮 Surprised','2%'],['😠 Angry','0%']].map(([e,p]) => (
            <div key={e} style={{ display:'grid', gridTemplateColumns:'16px 60px 1fr 24px', gap:4, alignItems:'center', marginBottom:3 }}>
              <span style={{ fontSize:'0.6rem' }}>{e.split(' ')[0]}</span>
              <span style={{ fontSize:'0.58rem', color:'#6b7280' }}>{e.split(' ')[1]}</span>
              <div style={{ height:2, background:'#f0f5f4', borderRadius:1 }}>
                <div style={{ height:'100%', width:p, background:'#0cb8a0', borderRadius:1 }}/>
              </div>
              <span style={{ fontSize:'0.55rem', color:'#9ca3af', textAlign:'right' }}>{p}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right — metrics */}
      <div style={{ display:'flex', flexDirection:'column', gap:0, background:'#ffffff', overflowY:'hidden' }}>
        {/* Attention */}
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #f0f5f4' }}>
          <div style={{ display:'flex', alignItems:'center', gap:10 }}>
            <AttentionArc score={attention}/>
            <div>
              <div style={{ fontSize:'0.52rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:2 }}>Attention Score</div>
              <div style={{ fontSize:'0.72rem', color:'#0cb8a0', fontWeight:600 }}>🎯 Focused</div>
              <div style={{ fontSize:'0.6rem', color:'#9ca3af' }}>Combined focus metric</div>
            </div>
          </div>
          <div style={{ marginTop:8, display:'grid', gridTemplateColumns:'1fr 1fr', gap:4 }}>
            {[['PERCLOS',`${perclos.toFixed(1)}%`],['Gaze','On screen'],['Head Yaw','2°'],['Yawns','0']].map(([l,v]) => (
              <div key={l} style={{ display:'flex', justifyContent:'space-between', fontSize:'0.58rem', padding:'2px 0' }}>
                <span style={{ color:'#9ca3af' }}>{l}</span>
                <span style={{ color:'#374151' }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Energy */}
        <div style={{ padding:'10px 12px', borderBottom:'1px solid #f0f5f4' }}>
          <div style={{ fontSize:'0.52rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Energy / Alertness</div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
            <span style={{ fontSize:'1.1rem' }}>⚡</span>
            <span style={{ fontFamily:"'Fraunces',serif", fontSize:'1rem', color:'#0cb8a0' }}>Energetic</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:6 }}>
            {[['12','BLINKS'],['15.2','/MIN'],['142','MS/BLINK']].map(([v,l]) => (
              <div key={l} style={{ background:'#f0f5f4', borderRadius:6, padding:'5px 4px', textAlign:'center' }}>
                <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1rem', color:'#0cb8a0' }}>{v}</div>
                <div style={{ fontSize:'0.48rem', color:'#9ca3af', textTransform:'uppercase' }}>{l}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Focus tracker */}
        <div style={{ padding:'10px 12px' }}>
          <div style={{ fontSize:'0.52rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:6 }}>Focus Tracker</div>
          <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom:6 }}>
            <div style={{ width:7, height:7, borderRadius:'50%', background:'#0cb8a0', boxShadow:'0 0 6px #0cb8a0' }}/>
            <span style={{ fontSize:'0.68rem', color:'#0cb8a0', fontWeight:600 }}>🎯 Focused</span>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6 }}>
            <div style={{ background:'#f0f5f4', borderRadius:8, padding:'8px', textAlign:'center' }}>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1rem', color:'#0cb8a0' }}>47m</div>
              <div style={{ fontSize:'0.48rem', color:'#9ca3af', textTransform:'uppercase' }}>Focused</div>
            </div>
            <div style={{ background:'#f0f5f4', borderRadius:8, padding:'8px', textAlign:'center' }}>
              <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1rem', color:'#f59e0b' }}>8m</div>
              <div style={{ fontSize:'0.48rem', color:'#9ca3af', textTransform:'uppercase' }}>Distracted</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Insights mockup ─────────────────────────────────────────────────────────
function InsightsMockup() {
  return (
    <div style={{ borderRadius:12, overflow:'hidden', border:'1px solid #e5e7eb', background:'#f9fafb', fontFamily:"'Nunito',sans-serif", fontSize:'0.7rem', padding:'14px 16px', display:'flex', flexDirection:'column', gap:10 }}>
      <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1rem', color:'#111827' }}>Today's Insights</div>

      {/* Score rings row */}
      <div style={{ display:'flex', gap:16, justifyContent:'center', padding:'4px 0' }}>
        {[['Session Quality','82',['#0cb8a0']],['Cognitive Load','31','#f59e0b'],['Emotion Stability','78','#3b82f6'],['Frustration','12','#0cb8a0']].map(([label, val, col]) => {
          const r = 20, circ = 2 * Math.PI * r;
          const fill = circ * (parseInt(val) / 100);
          return (
            <div key={label} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
              <svg width={52} height={52} style={{ transform:'rotate(-90deg)' }}>
                <circle cx={26} cy={26} r={r} fill="none" stroke="#e5e7eb" strokeWidth={5}/>
                <circle cx={26} cy={26} r={r} fill="none" stroke={col} strokeWidth={5}
                  strokeDasharray={`${fill} ${circ}`} strokeLinecap="round"/>
                <text x={26} y={26} textAnchor="middle" dominantBaseline="middle"
                  style={{ transform:'rotate(90deg)', transformOrigin:'26px 26px', fontSize:12, fill:col, fontWeight:'bold', fontFamily:'serif' }}>
                  {val}
                </text>
              </svg>
              <div style={{ fontSize:'0.48rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.05em', textAlign:'center', maxWidth:56 }}>{label}</div>
            </div>
          );
        })}
      </div>

      {/* Stat cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:6 }}>
        {[['Avg Attention','87','/100'],['Flow Time','34','min'],['Distractions','2',''],['Focus Streak','28','min']].map(([l,v,u]) => (
          <div key={l} style={{ background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:8, padding:'7px 8px', textAlign:'center' }}>
            <div style={{ fontFamily:"'Fraunces',serif", fontSize:'1rem', color:'#0cb8a0' }}>{v}<span style={{ fontSize:'0.55rem', color:'#9ca3af' }}>{u}</span></div>
            <div style={{ fontSize:'0.48rem', color:'#9ca3af', textTransform:'uppercase', marginTop:2 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Mini chart */}
      <div style={{ background:'#ffffff', border:'1px solid #e5e7eb', borderRadius:8, padding:'8px 10px', minHeight:70 }}>
        <div style={{ fontSize:'0.52rem', color:'#9ca3af', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:4 }}>Attention over time</div>
        <MiniChart data={FAKE_CHART}/>
      </div>
    </div>
  );
}

// ── Main export ─────────────────────────────────────────────────────────────
export function DemoPreview() {
  const [activeTab, setActiveTab] = useState('live');
  const [tick, setTick] = useState(0);
  const [emotionIdx, setEmotionIdx] = useState(0);
  const [attention, setAttention] = useState(85);
  const [coachIdx, setCoachIdx] = useState(0);

  // Simulate live updates
  useEffect(() => {
    const t = setInterval(() => {
      setTick(n => n + 1);
      setAttention(prev => {
        const delta = (Math.random() - 0.5) * 4;
        return Math.round(Math.max(72, Math.min(96, prev + delta)));
      });
    }, 2000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setEmotionIdx(i => (i + 1) % EMOTIONS.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setCoachIdx(i => (i + 1) % COACH_TIPS.length);
    }, 8000);
    return () => clearInterval(t);
  }, []);

  const emotion = EMOTIONS[emotionIdx];

  return (
    <div style={{ width:'100%', maxWidth:600, margin:'0 auto' }}>
      {/* Tab bar — mimics real app */}
      <div style={{ display:'flex', background:'#1a2332', borderRadius:'10px 10px 0 0', padding:'8px 12px', gap:4, alignItems:'center', justifyContent:'space-between' }}>
        <div style={{ fontFamily:"'Fraunces',serif", color:'#ffffff', fontSize:'0.85rem' }}>
          Mood<span style={{ color:'#0cb8a0', fontStyle:'italic' }}>scope</span>
        </div>
        <div style={{ display:'flex', background:'rgba(255,255,255,0.1)', borderRadius:6, padding:2, gap:2 }}>
          {[['live','📡 Live'],['insights','📊 Insights']].map(([tab, label]) => (
            <button key={tab} onClick={() => setActiveTab(tab)} style={{
              padding:'4px 12px', borderRadius:5, border:'none', cursor:'pointer',
              background: activeTab === tab ? 'rgba(255,255,255,0.15)' : 'transparent',
              color: activeTab === tab ? '#ffffff' : 'rgba(255,255,255,0.5)',
              fontSize:'0.62rem', fontFamily:"'Nunito',sans-serif",
            }}>{label}</button>
          ))}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:5 }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:'#0cb8a0', boxShadow:'0 0 8px #0cb8a0', animation:'pulse 2s infinite' }}/>
          <span style={{ color:'rgba(255,255,255,0.5)', fontSize:'0.58rem' }}>LIVE</span>
        </div>
      </div>

      {/* Content */}
      <div style={{ transition:'opacity 0.3s' }}>
        {activeTab === 'live'
          ? <LiveMockup emotion={emotion} attention={attention} tick={tick} coach={COACH_TIPS[coachIdx]}/>
          : <InsightsMockup/>
        }
      </div>

      {/* Caption */}
      <div style={{ textAlign:'center', marginTop:10, fontSize:'0.65rem', color:'#9ca3af' }}>
        {activeTab === 'live' ? '↑ Live tracking — updates every second' : '↑ Session insights after 1+ minute of tracking'}
        <span style={{ marginLeft:8, color:'#0cb8a0', cursor:'pointer' }} onClick={() => setActiveTab(activeTab === 'live' ? 'insights' : 'live')}>
          View {activeTab === 'live' ? 'Insights →' : '← Live view'}
        </span>
      </div>
    </div>
  );
}
